import { gameLabel } from "@loot-os/fulfillment-core/catalog/game";
import type { BuyingQueueRow } from "@loot-os/fulfillment-core/procurement/queue";
import {
  PROCUREMENT_UNIT_STATUSES,
  type ProcurementUnitStatus,
} from "@loot-os/fulfillment-core/procurement/unit-types";
import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { CommandItem } from "@loot-os/ui/components/command";
import { DataTable } from "@loot-os/ui/components/data-table";
import { Input } from "@loot-os/ui/components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { toast } from "@loot-os/ui/components/sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { createDataTableColumns } from "@loot-os/ui/lib/dataTable";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ErrorAlert } from "../../components/ErrorAlert";
import { LoadMoreButton } from "../../components/LoadMoreButton";
import { PickerCombobox } from "../../components/PickerCombobox";
import { centsToDollars, formatDate } from "../../lib/format";
import { useTRPC } from "../../lib/trpc";
import { useDebounced } from "../../lib/useDebounced";
import { AccountingUnitsPanel } from "./AccountingUnits";

const TABS = [
  { id: "queue", label: "Buying queue" },
  { id: "orders", label: "Buying orders" },
  { id: "receive", label: "Inventory adjustments" },
  { id: "uncosted", label: "Uncosted" },
  { id: "irregularities", label: "Irregularities" },
  { id: "units", label: "Units" },
  { id: "movements", label: "Movements" },
  { id: "accounting", label: "Accounting" },
] as const;

type TabId = (typeof TABS)[number]["id"];

interface CartLine {
  catalogItemId: string;
  name: string;
  image: string | null;
  quantity: number;
  marketCents: number;
}

interface CatalogPick {
  id: string;
  name: string;
  image: string | null;
  quantity: number;
}

const queueColumns = createDataTableColumns<BuyingQueueRow>();
const QUEUE_PINNED = { start: [], end: ["add"] };
/**
 * The queue fills what the viewport leaves after the page header, the action row, the card
 * header, the toolbar and the totals line, and never drops under 20rem. The rows scroll inside
 * it under a header that stays in view.
 */
const QUEUE_MAX_HEIGHT = "max(20rem, calc(100dvh - 26rem))";
/** Matches `BUYING_CART_SKU_CAP`. The server refuses a cart past this. */
const CART_SKU_CAP = 99;

/** Dollars typed with or without a `$`, as integer cents. Not a number when the text is not one. */
function parseInvoiceCents(text: string): number {
  const cleaned = text.replace(/[$,\s]/g, "");
  if (cleaned === "" || cleaned === ".") return 0;
  const dollars = Number(cleaned);
  if (!Number.isFinite(dollars) || dollars < 0) return Number.NaN;
  return Math.round(dollars * 100);
}
const dollars = (value: unknown) => centsToDollars(Number(value));

/** " · urgent: Pokemon 12, MTG 3" over the games with any urgent quantity; "" when none. */
function urgentByGameSummary(byGame: Record<string, number>): string {
  const parts = Object.entries(byGame)
    .filter(([, quantity]) => quantity > 0)
    .sort(([, a], [, b]) => b - a)
    .map(([game, quantity]) => `${gameLabel(game)} ${quantity}`);
  return parts.length ? ` · urgent: ${parts.join(", ")}` : "";
}

/** The queue's columns; `addToCart` is read through a ref so the column identity never changes. */
function buildQueueColumns(addToCart: { current: (row: BuyingQueueRow) => void }) {
  return queueColumns.columns([
    queueColumns.accessor("name", {
      header: "Item",
      // Wide enough for the photo and a truncated name. The rest are fitted to the header
      // and a typical value, so the row fits without the table's 150px default.
      size: 240,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          {row.original.image ? (
            <img src={row.original.image} alt="" className="h-10 w-10 shrink-0 object-contain" />
          ) : null}
          <div className="min-w-0">
            <div className="truncate font-medium" title={row.original.name}>
              {row.original.name}
            </div>
            <div className="truncate text-xs text-muted-foreground">
              {row.original.printing ?? "—"}{" "}
              {row.original.tcgPlayerUrl ? (
                <a
                  className="underline"
                  href={row.original.tcgPlayerUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  TCGPlayer
                </a>
              ) : null}
            </div>
          </div>
        </div>
      ),
    }),
    queueColumns.accessor("printing", {
      header: "Printing",
      size: 112,
      cell: ({ getValue }) => getValue() ?? "—",
      meta: { filter: "text" },
    }),
    queueColumns.accessor("urgency", {
      header: "Urgency",
      size: 88,
      cell: ({ getValue }) => getValue() ?? "—",
      meta: { align: "end", filter: "number" },
    }),
    queueColumns.accessor("targetOnHand", {
      header: "Qty target",
      meta: { hidden: true },
    }),
    queueColumns.accessor("toOrder", {
      header: "To order",
      size: 92,
      meta: { align: "end", filter: "number" },
    }),
    queueColumns.accessor("urgentQuantity", {
      header: "Urgent",
      size: 80,
      meta: { align: "end", filter: "number" },
    }),
    queueColumns.accessor((row) => (row.decision === "NOT_NEEDED" ? "Not needed" : row.decision), {
      id: "decision",
      header: "Decision",
      size: 112,
      meta: { filter: "text" },
    }),
    queueColumns.accessor("marketCents", {
      header: "Market",
      size: 84,
      cell: ({ getValue }) => centsToDollars(getValue()),
      meta: { align: "end", csv: dollars, filter: "number", filterCents: true },
    }),
    queueColumns.accessor("targetCents", {
      header: "Target",
      size: 84,
      cell: ({ getValue }) => centsToDollars(getValue()),
      meta: { align: "end", csv: dollars, filter: "number", filterCents: true },
    }),
    queueColumns.accessor("maxCents", {
      header: "Max",
      size: 76,
      cell: ({ getValue }) => centsToDollars(getValue()),
      meta: { align: "end", csv: dollars, filter: "number", filterCents: true },
    }),
    queueColumns.accessor("category", {
      header: "Category",
      size: 96,
      cell: ({ getValue }) => getValue() ?? "—",
      meta: { filter: "text" },
    }),
    queueColumns.accessor((row) => gameLabel(row.game) ?? "", {
      id: "game",
      header: "Game",
      size: 104,
      cell: ({ getValue }) => getValue() || "—",
      meta: { filter: "text" },
    }),
    queueColumns.accessor("tcgPlayerUrl", {
      header: "TCGPlayer URL",
      meta: { hidden: true, csv: (value) => (typeof value === "string" ? value : "") },
    }),
    queueColumns.display({
      id: "add",
      size: 72,
      cell: ({ row }) => (
        <Button size="sm" variant="outline" onClick={() => addToCart.current(row.original)}>
          Add
        </Button>
      ),
    }),
  ]);
}

/** Retool buying SOP: queue, cart, invoice split, packing list, dispositions. */
export function ProcurementPage() {
  const [tab, setTab] = useState<TabId>("queue");
  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Procurement</h1>
        <p className="text-sm text-muted-foreground">
          Standing needs, TCGPlayer buying, and per-card dispositions. Target is the market price.
          Max is the gem price, or 20% above it on a card under $5. ORDER may pay up to max
          including shipping, BUY_LOW up to target. Those are soft warnings.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {TABS.map((item) => (
          <Button
            key={item.id}
            size="sm"
            variant={tab === item.id ? "default" : "outline"}
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </Button>
        ))}
      </div>
      {tab === "queue" ? <QueuePanel /> : null}
      {tab === "orders" ? <OrdersPanel /> : null}
      {tab === "receive" ? <ReceivePanel /> : null}
      {tab === "uncosted" ? <UncostedPanel /> : null}
      {tab === "irregularities" ? <IrregularitiesPanel /> : null}
      {tab === "units" ? <UnitsAuditPanel /> : null}
      {tab === "movements" ? <MovementsAuditPanel /> : null}
      {tab === "accounting" ? <AccountingUnitsPanel /> : null}
    </div>
  );
}

/** The buying queue, kept out of the cart's renders so Add stays instant. */
const QueueTable = memo(function QueueTable({
  className,
  onAdd,
}: {
  className?: string;
  onAdd: (row: BuyingQueueRow) => void;
}) {
  const trpc = useTRPC();
  const queue = useQuery(trpc.platform.fulfillment.procurement.queue.queryOptions());
  const onAddRef = useRef(onAdd);
  onAddRef.current = onAdd;
  const columns = useMemo(() => buildQueueColumns(onAddRef), []);
  const rows = queue.data?.rows ?? [];
  const totals = queue.data?.totals;

  if (queue.isError) {
    return (
      <div className={className}>
        <ErrorAlert message={queue.error.message} />
      </div>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>Queue</CardTitle>
        {totals ? (
          <CardDescription>
            {totals.totalQuantity.toLocaleString()} cards · Queue at 80% of market:{" "}
            {centsToDollars(totals.queueValueAtEightyPercentCents)}
            {urgentByGameSummary(totals.urgentQuantityByGame)}
          </CardDescription>
        ) : null}
      </CardHeader>
      <CardContent>
        {queue.isPending ? (
          <Skeleton className="h-40" />
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            getRowId={(row) => row.catalogItemId}
            pinned={QUEUE_PINNED}
            search="Search items"
            csvName="buying-queue"
            emptyMessage="Nothing to buy."
            maxHeight={QUEUE_MAX_HEIGHT}
          />
        )}
      </CardContent>
    </Card>
  );
});

function QueuePanel() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [cart, setCart] = useState<CartLine[]>([]);
  const [vendor, setVendor] = useState("TCGPlayer");
  const [invoiceText, setInvoiceText] = useState("$0.00");
  const [unmatched, setUnmatched] = useState<Array<{ catalogItemId: string; quantity: number }>>(
    [],
  );
  const cartRef = useRef(cart);
  const commitCart = useCallback((next: CartLine[]) => {
    cartRef.current = next;
    setCart(next);
  }, []);

  const refresh = () =>
    queryClient.invalidateQueries({
      queryKey: trpc.platform.fulfillment.procurement.pathKey(),
    });

  const submit = useMutation(
    trpc.platform.fulfillment.procurement.orders.create.mutationOptions({
      onSuccess: async () => {
        toast.success("Buying order created.");
        commitCart([]);
        setInvoiceText("$0.00");
        await refresh();
      },
    }),
  );
  const packing = useMutation(
    trpc.platform.fulfillment.procurement.orders.uploadPackingList.mutationOptions({
      onSuccess: async (result) => {
        setUnmatched(result.unmatched);
        if (result.order) toast.success("Packing list submitted.");
        else toast.success("No matched rows to order.");
        await refresh();
      },
    }),
  );

  const addToCart = useCallback(
    (row: BuyingQueueRow) => {
      const prev = cartRef.current;
      const existing = prev.find((line) => line.catalogItemId === row.catalogItemId);
      if (!existing && prev.length >= CART_SKU_CAP) {
        toast.error(`Cart is capped at ${CART_SKU_CAP} SKUs.`);
        return;
      }
      const next = existing
        ? prev.map((line) =>
            line.catalogItemId === row.catalogItemId
              ? { ...line, quantity: line.quantity + 1 }
              : line,
          )
        : [
            ...prev,
            {
              catalogItemId: row.catalogItemId,
              name: row.name,
              image: row.image,
              quantity: row.urgentQuantity || 1,
              marketCents: row.marketCents,
            },
          ];
      // Written before the re-render so a second Add in the same moment sees this line.
      commitCart(next);
    },
    [commitCart],
  );

  const invoiceCents = parseInvoiceCents(invoiceText);
  const cartQuantity = cart.reduce((sum, line) => sum + line.quantity, 0);

  return (
    <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_28rem]">
      <QueueTable className="min-w-0" onAdd={addToCart} />
      <div className="flex flex-col gap-4">
        <Card>
          <CardHeader>
            <CardTitle>
              Cart ({cart.length}
              {cart.length > 0 ? ` · ${cartQuantity.toLocaleString()} cards` : ""})
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {cart.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nothing in the cart.</p>
            ) : (
              <Table containerClassName="max-h-80 overflow-y-auto">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-14">Image</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="w-36">Quantity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cart.map((line) => (
                    <TableRow key={line.catalogItemId}>
                      <TableCell>
                        {line.image ? (
                          <img src={line.image} alt="" className="h-10 w-10 object-contain" />
                        ) : (
                          <div className="h-10 w-10 rounded bg-muted" />
                        )}
                      </TableCell>
                      <TableCell className="max-w-0">
                        <span className="block truncate font-medium" title={line.name}>
                          {line.name}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <CartQuantityInput
                            name={line.name}
                            quantity={line.quantity}
                            onQuantity={(quantity) =>
                              commitCart(
                                cartRef.current.map((item) =>
                                  item.catalogItemId === line.catalogItemId
                                    ? { ...item, quantity }
                                    : item,
                                ),
                              )
                            }
                          />
                          <Button
                            size="icon-sm"
                            variant="ghost"
                            aria-label={`Remove ${line.name}`}
                            onClick={() =>
                              commitCart(
                                cartRef.current.filter(
                                  (item) => item.catalogItemId !== line.catalogItemId,
                                ),
                              )
                            }
                          >
                            <X />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={vendor}
                onChange={(event) => setVendor(event.target.value)}
                aria-label="Vendor"
                className="h-8 w-40"
              />
              <Input
                aria-label="Invoice total"
                inputMode="decimal"
                value={invoiceText}
                onChange={(event) => setInvoiceText(event.target.value)}
                onBlur={(event) => {
                  const cents = parseInvoiceCents(event.currentTarget.value);
                  setInvoiceText(Number.isFinite(cents) ? centsToDollars(cents) : "$0.00");
                }}
                className="h-8 w-32"
              />
              <Button
                disabled={
                  submit.isPending ||
                  cart.length === 0 ||
                  !Number.isFinite(invoiceCents) ||
                  invoiceCents <= 0
                }
                onClick={() =>
                  submit.mutate({
                    vendor,
                    invoiceTotalCents: invoiceCents,
                    lines: cart.map((line) => ({
                      catalogItemId: line.catalogItemId,
                      quantity: line.quantity,
                      marketCents: line.marketCents,
                    })),
                  })
                }
              >
                {submit.isPending ? "Submitting…" : "Submit order"}
              </Button>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Packing list</CardTitle>
          </CardHeader>
          <CardContent>
            <Input
              type="file"
              accept=".csv,text/csv"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                void file
                  .text()
                  .then((csvText) => packing.mutate({ csvText, filename: file.name }));
              }}
            />
            {unmatched.length > 0 ? (
              <ul className="mt-3 text-sm text-muted-foreground">
                {unmatched.map((row) => (
                  <li key={row.catalogItemId}>
                    Unmatched {row.catalogItemId} × {row.quantity}
                  </li>
                ))}
              </ul>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/**
 * A plain digits field. The text stays whatever was typed, including a blank while the last
 * digit is replaced, and the cart takes a whole number of at least 1 once one is there.
 */
function CartQuantityInput({
  name,
  quantity,
  onQuantity,
}: {
  name: string;
  quantity: number;
  onQuantity: (quantity: number) => void;
}) {
  const [text, setText] = useState(String(quantity));
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) setText(String(quantity));
  }, [quantity]);

  return (
    <Input
      aria-label={`Quantity for ${name}`}
      inputMode="numeric"
      autoComplete="off"
      className="h-8 w-16 text-right tabular-nums"
      value={text}
      onFocus={() => {
        focused.current = true;
      }}
      onBlur={(event) => {
        focused.current = false;
        const parsed = Number(event.currentTarget.value);
        if (!Number.isInteger(parsed) || parsed < 1) {
          setText(String(quantity));
          return;
        }
        onQuantity(parsed);
        setText(String(parsed));
      }}
      onChange={(event) => {
        const next = event.target.value;
        if (next !== "" && !/^\d+$/.test(next)) return;
        setText(next);
        const parsed = Number(next);
        if (Number.isInteger(parsed) && parsed >= 1) onQuantity(parsed);
      }}
    />
  );
}

function delayLabel(orderDate: Date | string): string {
  const ms = Date.now() - new Date(orderDate).getTime();
  const days = Math.max(0, Math.floor(ms / (24 * 60 * 60 * 1000)));
  return `${days}d`;
}

function OrdersPanel() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const orders = useQuery(trpc.platform.fulfillment.procurement.orders.list.queryOptions());
  const cancel = useMutation(
    trpc.platform.fulfillment.procurement.orders.cancel.mutationOptions({
      onSuccess: async () => {
        toast.success("Buying order cancelled.");
        await queryClient.invalidateQueries({
          queryKey: trpc.platform.fulfillment.procurement.pathKey(),
        });
      },
    }),
  );
  if (orders.isError) return <ErrorAlert message={orders.error.message} />;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent buying orders</CardTitle>
      </CardHeader>
      <CardContent>
        {orders.isPending ? (
          <Skeleton className="h-32" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Vendor</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Delay</TableHead>
                <TableHead>Invoice</TableHead>
                <TableHead>Units</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {(orders.data ?? []).map((order) => (
                <TableRow key={order.id}>
                  <TableCell>{order.vendor}</TableCell>
                  <TableCell>{order.source}</TableCell>
                  <TableCell>
                    {order.source === "BUYLIST" && order.buylistStatus
                      ? `${order.status} / ${order.buylistStatus}`
                      : order.status}
                  </TableCell>
                  <TableCell>{delayLabel(order.orderDate)}</TableCell>
                  <TableCell>{centsToDollars(order.invoiceTotalCents)}</TableCell>
                  <TableCell>{order.unitCount}</TableCell>
                  <TableCell>
                    {order.source === "INTERNAL" && order.status === "ORDERED" ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => cancel.mutate({ id: order.id })}
                      >
                        Undo
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

const ADJUSTMENT_KINDS = ["DAMAGED", "COUNT_ADJUSTMENT", "CUSTOMER_SUPPORT"] as const;
type AdjustmentKind = (typeof ADJUSTMENT_KINDS)[number];

const ADJUSTMENT_KIND_LABELS: Record<AdjustmentKind, string> = {
  DAMAGED: "Damaged",
  COUNT_ADJUSTMENT: "Count adjustment",
  CUSTOMER_SUPPORT: "Customer support",
};

const SUPPORT_REASON_LABELS: Record<string, string> = {
  RESHIPMENT: "Reshipment",
  VIP: "VIP",
  MARKETING: "Marketing",
  OTHER: "Other",
};

function ReceivePanel() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [picked, setPicked] = useState<CatalogPick | null>(null);
  const [quantity, setQuantity] = useState("1");
  const [kind, setKind] = useState<AdjustmentKind>("DAMAGED");
  const [supportReason, setSupportReason] = useState<string>("RESHIPMENT");
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: trpc.platform.fulfillment.procurement.pathKey(),
      }),
      queryClient.invalidateQueries({ queryKey: trpc.platform.fulfillment.items.pathKey() }),
    ]);
  const adjust = useMutation(
    trpc.platform.fulfillment.procurement.units.adjust.mutationOptions({
      onSuccess: async (result, variables) => {
        const asked = variables.quantity;
        if (result.adjusted === 0) toast.error("No on-hand units to adjust.");
        else if (result.adjusted < asked) {
          toast.success(`Adjusted ${result.adjusted}. That was every on-hand unit.`);
        } else toast.success(`Adjusted ${result.adjusted}.`);
        setPicked((current) =>
          current
            ? { ...current, quantity: Math.max(0, current.quantity - result.adjusted) }
            : current,
        );
        await refresh();
      },
    }),
  );
  const adjustQuantity = Number(quantity);
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>From inventory</CardTitle>
          <CardDescription>
            Look up a catalog item, choose the adjustment, and apply it to that many on-hand units.
            Stock is received on Inventory.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <CatalogLookup picked={picked} onPick={setPicked} />
          {picked ? (
            <div className="flex items-center gap-3">
              {picked.image ? (
                <img src={picked.image} alt="" className="h-12 w-12 object-contain" />
              ) : (
                <div className="h-12 w-12 rounded bg-muted" />
              )}
              <div>
                <div className="font-medium">{picked.name}</div>
                <div className="text-sm text-muted-foreground">On hand {picked.quantity}</div>
              </div>
            </div>
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            <Select
              items={ADJUSTMENT_KIND_LABELS}
              value={kind}
              onValueChange={(value) => {
                if (value) setKind(value as AdjustmentKind);
              }}
            >
              <SelectTrigger size="sm" className="w-48" aria-label="Adjustment kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ADJUSTMENT_KINDS.map((value) => (
                  <SelectItem key={value} value={value}>
                    {ADJUSTMENT_KIND_LABELS[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {kind === "CUSTOMER_SUPPORT" ? (
              <Select
                items={SUPPORT_REASON_LABELS}
                value={supportReason}
                onValueChange={(value) => {
                  if (value) setSupportReason(value);
                }}
              >
                <SelectTrigger size="sm" className="w-40" aria-label="Customer support reason">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(SUPPORT_REASON_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
            <Input
              aria-label="Quantity to adjust"
              inputMode="numeric"
              value={quantity}
              onChange={(event) => {
                const next = event.target.value;
                if (next !== "" && !/^\d+$/.test(next)) return;
                setQuantity(next);
              }}
              className="h-8 w-24"
            />
            <Button
              disabled={
                !picked ||
                !Number.isInteger(adjustQuantity) ||
                adjustQuantity < 1 ||
                adjust.isPending
              }
              onClick={() => {
                if (!picked) return;
                adjust.mutate({
                  catalogItemId: picked.id,
                  quantity: adjustQuantity,
                  kind,
                  supportReason:
                    kind === "CUSTOMER_SUPPORT"
                      ? (supportReason as "RESHIPMENT" | "VIP" | "MARKETING" | "OTHER")
                      : undefined,
                });
              }}
            >
              {adjust.isPending ? "Adjusting…" : "Adjust"}
            </Button>
          </div>
        </CardContent>
      </Card>
      <InTransitDamageCard />
    </div>
  );
}

/** Look up one in-transit catalog item and mark a quantity of its units damaged. */
function InTransitDamageCard() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [picked, setPicked] = useState<CatalogPick | null>(null);
  const [quantity, setQuantity] = useState("1");
  const damage = useMutation(
    trpc.platform.fulfillment.procurement.units.damageInTransit.mutationOptions({
      onSuccess: async (result, variables) => {
        const asked = variables.quantity;
        if (result.damaged === 0) toast.error("No in-transit units to mark damaged.");
        else if (result.damaged < asked) {
          toast.success(`Marked ${result.damaged} damaged. That was every in-transit unit.`);
        } else toast.success(`Marked ${result.damaged} damaged.`);
        setPicked((current) =>
          current
            ? { ...current, quantity: Math.max(0, current.quantity - result.damaged) }
            : current,
        );
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.platform.fulfillment.procurement.pathKey(),
          }),
          queryClient.invalidateQueries({ queryKey: trpc.platform.fulfillment.items.pathKey() }),
        ]);
      },
    }),
  );
  const damageQuantity = Number(quantity);
  return (
    <Card>
      <CardHeader>
        <CardTitle>In transit</CardTitle>
        <CardDescription>
          Look up a catalog item that is in transit and mark that many units arrived damaged.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <InTransitLookup picked={picked} onPick={setPicked} />
        {picked ? (
          <div className="flex items-center gap-3">
            {picked.image ? (
              <img src={picked.image} alt="" className="h-12 w-12 object-contain" />
            ) : (
              <div className="h-12 w-12 rounded bg-muted" />
            )}
            <div>
              <div className="font-medium">{picked.name}</div>
              <div className="text-sm text-muted-foreground">In transit {picked.quantity}</div>
            </div>
          </div>
        ) : null}
        <div className="flex flex-wrap items-center gap-2">
          <Input
            aria-label="In-transit quantity damaged"
            inputMode="numeric"
            value={quantity}
            onChange={(event) => {
              const next = event.target.value;
              if (next !== "" && !/^\d+$/.test(next)) return;
              setQuantity(next);
            }}
            className="h-8 w-24"
          />
          <Button
            disabled={
              !picked || !Number.isInteger(damageQuantity) || damageQuantity < 1 || damage.isPending
            }
            onClick={() => {
              if (!picked) return;
              damage.mutate({ catalogItemId: picked.id, quantity: damageQuantity });
            }}
          >
            {damage.isPending ? "Marking…" : "Arrived damaged"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function UncostedPanel() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [query, setQuery] = useState("");
  const debounced = useDebounced(query);
  const searching = debounced.trim().length > 0;
  const rows = useQuery({
    ...trpc.platform.fulfillment.procurement.units.searchUncosted.queryOptions({
      query: debounced.trim(),
    }),
    enabled: searching,
  });
  const setCost = useMutation(
    trpc.platform.fulfillment.procurement.units.setCost.mutationOptions({
      onSuccess: async () => {
        toast.success("Cost saved.");
        await queryClient.invalidateQueries({
          queryKey: trpc.platform.fulfillment.procurement.pathKey(),
        });
      },
    }),
  );
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  return (
    <Card>
      <CardHeader>
        <CardTitle>Items without a purchase price</CardTitle>
        <CardDescription>Look up a catalog item to see its uncosted units.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <Input
          aria-label="Look up an uncosted catalog item"
          placeholder="Look up a catalog item"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          className="h-8 max-w-md"
        />
        {!searching ? (
          <p className="text-sm text-muted-foreground">Type a name to search.</p>
        ) : rows.isPending ? (
          <Skeleton className="h-32" />
        ) : rows.isError ? (
          <ErrorAlert message={rows.error.message} />
        ) : (rows.data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No uncosted units match.</p>
        ) : (
          <Table containerClassName="max-h-[32rem] overflow-y-auto">
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Cost</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {(rows.data ?? []).map((unit) => (
                <TableRow key={unit.id}>
                  <TableCell>
                    <ItemLabel name={unit.name} image={unit.image} />
                  </TableCell>
                  <TableCell>{unit.costSource}</TableCell>
                  <TableCell>
                    <Input
                      value={drafts[unit.id] ?? String(unit.unitCostCents / 100)}
                      onChange={(event) =>
                        setDrafts((prev) => ({ ...prev, [unit.id]: event.target.value }))
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      onClick={() =>
                        setCost.mutate({
                          id: unit.id,
                          unitCostCents: Math.round(
                            Number(drafts[unit.id] ?? unit.unitCostCents / 100) * 100,
                          ),
                        })
                      }
                    >
                      Save
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function IrregularitiesPanel() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const snap = useQuery(trpc.platform.fulfillment.procurement.irregularities.queryOptions());
  const damaged = useQuery(
    trpc.platform.fulfillment.procurement.units.listByStatus.queryOptions({ status: "DAMAGED" }),
  );
  const recover = useMutation(
    trpc.platform.fulfillment.procurement.units.recoverDamaged.mutationOptions({
      onSuccess: async () => {
        toast.success("Recovered to inventory.");
        await queryClient.invalidateQueries({
          queryKey: trpc.platform.fulfillment.procurement.pathKey(),
        });
      },
    }),
  );
  if (snap.isError) return <ErrorAlert message={snap.error.message} />;
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Latest snapshot</CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          {snap.data ? (
            <ul className="space-y-1">
              <li>Day {snap.data.day}</li>
              <li>Damaged {centsToDollars(snap.data.damagedCostCents)}</li>
              <li>Lost in transit {centsToDollars(snap.data.lostInTransitCostCents)}</li>
              <li>Customer support {centsToDollars(snap.data.customerSupportCostCents)}</li>
              <li>Count adjustment {centsToDollars(snap.data.countAdjustmentCostCents)}</li>
            </ul>
          ) : (
            <p className="text-muted-foreground">No snapshot yet.</p>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Damaged units</CardTitle>
        </CardHeader>
        <CardContent>
          {damaged.isError ? (
            <ErrorAlert message={damaged.error.message} />
          ) : damaged.isPending ? (
            <Skeleton className="h-32 w-full" />
          ) : damaged.data?.length === 0 ? (
            <p className="text-sm text-muted-foreground">No damaged units.</p>
          ) : (
            <Table containerClassName="max-h-[32rem] overflow-y-auto">
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {damaged.data?.map((unit) => (
                  <TableRow key={unit.id}>
                    <TableCell>
                      <ItemLabel name={unit.name} image={unit.image} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => recover.mutate({ id: unit.id })}
                      >
                        Recover
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const AUDIT_PAGE_SIZE = 50;
const ANY_STATUS = "__any";

const UNIT_STATUS_LABELS: Record<string, string> = {
  [ANY_STATUS]: "All statuses",
  ...Object.fromEntries(PROCUREMENT_UNIT_STATUSES.map((status) => [status, status])),
};

/** Name and status filters shared by the two audit tables. */
function AuditFilters({
  query,
  onQuery,
  status,
  onStatus,
  queryLabel,
}: {
  query: string;
  onQuery: (value: string) => void;
  status: ProcurementUnitStatus | typeof ANY_STATUS;
  onStatus: (value: ProcurementUnitStatus | typeof ANY_STATUS) => void;
  queryLabel: string;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Input
        aria-label={queryLabel}
        className="w-72"
        placeholder="Catalog name contains…"
        value={query}
        onChange={(event) => onQuery(event.target.value)}
      />
      <Select
        items={UNIT_STATUS_LABELS}
        value={status}
        onValueChange={(value) => {
          if (value) onStatus(value as ProcurementUnitStatus | typeof ANY_STATUS);
        }}
      >
        <SelectTrigger size="sm" className="w-52" aria-label="Status">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(UNIT_STATUS_LABELS).map(([value, label]) => (
            <SelectItem key={value} value={value}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function useAuditFilters() {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<ProcurementUnitStatus | typeof ANY_STATUS>(ANY_STATUS);
  const name = useDebounced(query.trim(), 300);
  return {
    query,
    setQuery,
    status,
    setStatus,
    name: name || undefined,
    statusFilter: status === ANY_STATUS ? undefined : status,
  };
}

/** Every procurement unit, newest change first, paged so the whole table can be walked. */
function UnitsAuditPanel() {
  const trpc = useTRPC();
  const filters = useAuditFilters();
  const page = useInfiniteQuery(
    trpc.platform.fulfillment.procurement.units.browse.infiniteQueryOptions(
      { limit: AUDIT_PAGE_SIZE, query: filters.name, status: filters.statusFilter },
      { getNextPageParam: (last) => last.nextCursor },
    ),
  );
  const units = page.data?.pages.flatMap((item) => item.units) ?? [];
  return (
    <Card>
      <CardHeader>
        <CardTitle>Procurement units</CardTitle>
        <CardDescription>
          Every physical card. {units.length.toLocaleString()}
          {page.hasNextPage ? "+" : ""} loaded, newest change first.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <AuditFilters
          query={filters.query}
          onQuery={filters.setQuery}
          status={filters.status}
          onStatus={filters.setStatus}
          queryLabel="Catalog name"
        />
        {page.isError ? (
          <ErrorAlert message={page.error.message} />
        ) : page.isPending ? (
          <Skeleton className="h-40 w-full" />
        ) : units.length === 0 ? (
          <p className="text-sm text-muted-foreground">No units match.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Cost</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Origin</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead>Id</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {units.map((unit) => (
                  <TableRow key={unit.id}>
                    <TableCell>
                      <ItemLabel name={unit.name} image={unit.image} />
                    </TableCell>
                    <TableCell>{unit.status}</TableCell>
                    <TableCell>{centsToDollars(unit.unitCostCents)}</TableCell>
                    <TableCell>{unit.costSource}</TableCell>
                    <TableCell>{unit.damageOrigin ?? "—"}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {formatDate(unit.createdAt)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {formatDate(unit.updatedAt)}
                    </TableCell>
                    <TableCell className="font-mono text-xs" title={unit.id}>
                      {unit.id.slice(0, 8)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <LoadMoreButton
          hasNextPage={page.hasNextPage}
          isFetchingNextPage={page.isFetchingNextPage}
          onClick={() => void page.fetchNextPage()}
        />
      </CardContent>
    </Card>
  );
}

/** Every inventory movement, newest first. Status filters the status the unit moved to. */
function MovementsAuditPanel() {
  const trpc = useTRPC();
  const filters = useAuditFilters();
  const page = useInfiniteQuery(
    trpc.platform.fulfillment.procurement.units.movements.infiniteQueryOptions(
      { limit: AUDIT_PAGE_SIZE, query: filters.name, status: filters.statusFilter },
      { getNextPageParam: (last) => last.nextCursor },
    ),
  );
  const movements = page.data?.pages.flatMap((item) => item.movements) ?? [];
  return (
    <Card>
      <CardHeader>
        <CardTitle>Inventory movements</CardTitle>
        <CardDescription>
          Every status change. {movements.length.toLocaleString()}
          {page.hasNextPage ? "+" : ""} loaded, newest first. Status is the status moved to.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <AuditFilters
          query={filters.query}
          onQuery={filters.setQuery}
          status={filters.status}
          onStatus={filters.setStatus}
          queryLabel="Catalog name"
        />
        {page.isError ? (
          <ErrorAlert message={page.error.message} />
        ) : page.isPending ? (
          <Skeleton className="h-40 w-full" />
        ) : movements.length === 0 ? (
          <p className="text-sm text-muted-foreground">No movements match.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Unit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {movements.map((movement) => (
                  <TableRow key={movement.id}>
                    <TableCell className="whitespace-nowrap">
                      {formatDate(movement.createdAt)}
                    </TableCell>
                    <TableCell>
                      <ItemLabel name={movement.name} image={movement.image} />
                    </TableCell>
                    <TableCell>{movement.fromStatus ?? "—"}</TableCell>
                    <TableCell>{movement.toStatus}</TableCell>
                    <TableCell>{movement.actorType}</TableCell>
                    <TableCell>{movement.reason ?? "—"}</TableCell>
                    <TableCell className="font-mono text-xs" title={movement.procurementId}>
                      {movement.procurementId.slice(0, 8)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <LoadMoreButton
          hasNextPage={page.hasNextPage}
          isFetchingNextPage={page.isFetchingNextPage}
          onClick={() => void page.fetchNextPage()}
        />
      </CardContent>
    </Card>
  );
}

function ItemLabel({ name, image }: { name: string; image: string | null }) {
  return (
    <div className="flex items-center gap-2">
      {image ? (
        <img src={image} alt="" className="h-10 w-10 shrink-0 object-contain" />
      ) : (
        <div className="h-10 w-10 shrink-0 rounded bg-muted" />
      )}
      <span className="min-w-0 truncate font-medium" title={name}>
        {name}
      </span>
    </div>
  );
}

/** In-transit catalog items whose name matches. One result per item, with how many are in transit. */
function InTransitLookup({
  picked,
  onPick,
}: {
  picked: CatalogPick | null;
  onPick: (item: CatalogPick) => void;
}) {
  const trpc = useTRPC();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const debounced = useDebounced(query);
  const searching = debounced.trim().length > 0;
  const results = useQuery({
    ...trpc.platform.fulfillment.procurement.units.searchInTransit.queryOptions({
      query: debounced.trim(),
    }),
    enabled: open && searching,
  });

  return (
    <div className="max-w-md">
      <PickerCombobox
        open={open}
        onOpenChange={setOpen}
        query={query}
        onQueryChange={setQuery}
        triggerLabel={picked?.name ?? "Look up an in-transit item"}
        searchPlaceholder="Search in-transit items…"
        emptyMessage={
          searching
            ? results.isFetching
              ? "Searching…"
              : (results.error?.message ?? "No in-transit items match.")
            : "Type a name to search."
        }
      >
        {(results.data ?? []).map((item) => (
          <CommandItem
            key={item.catalogItemId}
            value={item.catalogItemId}
            onSelect={() => {
              onPick({
                id: item.catalogItemId,
                name: item.name,
                image: item.image,
                quantity: item.quantity,
              });
              setOpen(false);
            }}
          >
            {item.image ? (
              <img src={item.image} alt="" className="size-8 shrink-0 object-contain" />
            ) : (
              <div className="size-8 shrink-0 rounded bg-muted" />
            )}
            <span className="truncate">
              {item.name} · {item.quantity}
            </span>
          </CommandItem>
        ))}
      </PickerCombobox>
    </div>
  );
}

/** Catalog search for the damage form. Results carry the photo and name staff pick from. */
function CatalogLookup({
  picked,
  onPick,
}: {
  picked: CatalogPick | null;
  onPick: (item: CatalogPick) => void;
}) {
  const trpc = useTRPC();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const debounced = useDebounced(query);
  const searching = debounced.trim().length > 0;
  const results = useQuery({
    ...trpc.platform.fulfillment.items.list.queryOptions({
      ...(searching ? { query: debounced.trim() } : {}),
      limit: 20,
    }),
    enabled: open && searching,
  });

  return (
    <div className="max-w-md">
      <PickerCombobox
        open={open}
        onOpenChange={setOpen}
        query={query}
        onQueryChange={setQuery}
        triggerLabel={picked?.name ?? "Look up a catalog item"}
        searchPlaceholder="Search the catalog…"
        emptyMessage={
          searching
            ? results.isFetching
              ? "Searching…"
              : (results.error?.message ?? "No matching catalog items.")
            : "Type a name to search."
        }
      >
        {(results.data?.items ?? []).map((item) => {
          const image = item.overrideImage ?? item.image;
          return (
            <CommandItem
              key={item.id}
              value={item.id}
              onSelect={() => {
                onPick({ id: item.id, name: item.name, image, quantity: item.quantity });
                setOpen(false);
              }}
            >
              {image ? (
                <img src={image} alt="" className="size-8 shrink-0 object-contain" />
              ) : (
                <div className="size-8 shrink-0 rounded bg-muted" />
              )}
              <span className="truncate">{item.name}</span>
            </CommandItem>
          );
        })}
      </PickerCombobox>
    </div>
  );
}
